import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, Share, SquarePlus, X } from "lucide-react";

const RESERVED = new Set(["", "login", "signup", "forgot-password", "reset-password", "unsubscribe", "privacy", "privacy-policy", "about", "vs", "fan-terms", "dpa",
  "referral-terms", "pricing", "blog", "terms", "cookies", "my-data", "c"]);
const DISMISS_KEY = "famclaim-install-dismissed";

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isPhone = () => isIOS() || /android|mobile/i.test(navigator.userAgent);
const isInstalled = () => window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true;

const allowedPath = (path) => !RESERVED.has(path.split("/")[1] || "");

export function InstallPrompt() {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(() => window.__famclaimInstallPrompt || null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(DISMISS_KEY) === "1" || isInstalled());

  useEffect(() => {
    const onReady = () => setDeferred(window.__famclaimInstallPrompt);
    const onInstalled = () => setHidden(true);
    window.addEventListener("famclaim-install-ready", onReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("famclaim-install-ready", onReady);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const ios = isIOS();
  if (hidden || !isPhone() || !allowedPath(pathname) || (!ios && !deferred)) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setHidden(true);
  };

  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    window.__famclaimInstallPrompt = null;
    setDeferred(null);
    if (outcome === "accepted") setHidden(true);
  };

  return (
    <div data-testid="install-prompt"
      className="fixed inset-x-3 bottom-24 z-50 animate-in fade-in slide-in-from-bottom-4 rounded-2xl border border-border bg-card/95 p-4 text-left shadow-2xl backdrop-blur-xl duration-300">
      <button data-testid="install-prompt-close" onClick={dismiss} aria-label="Close"
        className="absolute right-3 top-3 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <img src="/icon-192.png" alt="Famclaim" className="h-11 w-11 shrink-0 rounded-xl" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Add Famclaim to your home screen</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Open it in one tap, full screen, just like an app.</p>
        </div>
      </div>
      {ios ? <IOSSteps /> : (
        <button data-testid="install-prompt-install" onClick={install}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground transition-transform active:scale-[0.98]">
          <Download className="h-4 w-4" /> Add to home screen
        </button>
      )}
    </div>
  );
}

const IOSSteps = () => (
  <ol data-testid="install-prompt-ios-steps" className="mt-3 space-y-1.5 rounded-xl bg-muted/50 p-3 text-xs text-foreground">
    <li className="flex items-center gap-2">
      <span className="font-semibold">1.</span> Tap <Share className="h-4 w-4 text-primary" /> Share in Safari's toolbar
    </li>
    <li className="flex items-center gap-2">
      <span className="font-semibold">2.</span> Choose <SquarePlus className="h-4 w-4 text-primary" /> Add to Home Screen
    </li>
  </ol>
);
